import { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Link } from 'react-router-dom';
import { getCameraActions, PageLayout } from 'components/PageLayout';
import { useMembershipState } from 'state/useMembershipState';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import { useAuthState } from 'state/useAuthState';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { useMembershipSuccessQueryParams } from 'features/membership/hooks/useMembershipUrlParams';
import { WATCHDUTY_SCHEME } from '../../../constants';

const useStyles = makeStyles()(() => ({
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  atag: {
    textDecoration: 'none',
    width: '100%',
  },
}));

const getMembershipSuccessButtonLink = (input: {
  newUserMembership: boolean;
  isMembershipUpgrade: boolean;
  clientToken: string;
}): string => {
  const { newUserMembership, isMembershipUpgrade, clientToken } = input;
  if (newUserMembership) {
    return `/?clientToken=${clientToken}`;
  }
  if (isMembershipUpgrade) {
    return '/?membershipUpgrade=true';
  }
  return '/?membershipSuccess=true';
};

const Content = (): JSX.Element => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { clientToken, fromMobile, resetMembershipState } =
    useMembershipState();
  const fromMobileRef = useRef<boolean>(fromMobile);
  const clientTokenRef = useRef<string>(clientToken);
  const { isAuthenticated } = useAuthState();

  const newUserMembership = Boolean(clientTokenRef.current);
  const { upgrade: isMembershipUpgrade } = useMembershipSuccessQueryParams();

  useEffect(() => {
    // send explicit GA events for membership success
    logEvent({
      name: ANALYTICS_EVENTS.SUPPORT.PURCHASE,
      params: {},
    });
    logEvent({
      name: ANALYTICS_EVENTS.SUPPORT.PURCHASE_MEMBERSHIP,
      params: {},
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      persistedQueryClient.invalidateQueries({ queryKey: ['auth-user'] });
    }
  }, [isAuthenticated]);

  return (
    <>
      <Helmet>
        <title>{t('membershipSuccess.title')}</title>
      </Helmet>

      <SimplePageLayout>
        <Box className={classes.contentContainer}>
          <Typography variant="h2" align="center" sx={{ mb: 4 }}>
            <b>{t('membershipSuccess.subtitle')}</b>
          </Typography>

          <Typography align="center" sx={{ mb: 3 }}>
            {t(
              `membershipSuccess.thanks.${
                isMembershipUpgrade ? 'pro' : 'regular'
              }`,
            )}
          </Typography>

          {newUserMembership && (
            <Typography align="center">
              {t('membershipSuccess.message')}
            </Typography>
          )}
        </Box>

        {/* The react-dom-router link component does not consistently open the custom scheme urls correctly, use explicit A tag */}
        {fromMobileRef.current ? (
          <a
            href={`${WATCHDUTY_SCHEME}app.watchduty.org${getMembershipSuccessButtonLink(
              {
                newUserMembership,
                isMembershipUpgrade,
                clientToken: clientTokenRef.current,
              },
            )}`}
            target="_blank"
            className={classes.atag}
            rel="noreferrer"
          >
            <Button
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              onClick={resetMembershipState}
            >
              {t('membershipSuccess.button')}
            </Button>
          </a>
        ) : (
          <Button
            fullWidth
            size="large"
            sx={{ mt: 2 }}
            component={Link}
            to={getMembershipSuccessButtonLink({
              newUserMembership,
              isMembershipUpgrade,
              clientToken: clientTokenRef.current,
            })}
            onClick={resetMembershipState}
          >
            {t('membershipSuccess.button')}
          </Button>
        )}
      </SimplePageLayout>
    </>
  );
};

export const MembershipSuccess = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <PageLayout
      data-cname="MembershipSuccessPage"
      allowAppBanner={false}
      headerBarProps={{
        action: 'noControls',
        iconActions: getCameraActions(t),
      }}
    >
      <Content />
    </PageLayout>
  );
};
